import { Box, Button, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { format } from 'date-fns';
import { useMemo } from 'react';
import {
  AvailabilityStateType,
  AvailabilityState,
} from '@src/constants/AvailabilityState';
import { JobBookingStatus } from '@src/constants/JobBookingStatus';
import UserAvailabilityDTO from '@src/adapters/dto/user/UserAvailabilityDTO';
import { formatDate } from '@src/utils/dateUtils';

const PREFIX = 'availability';

const classes = {
  title: `${PREFIX}-title`,
  subtitle: `${PREFIX}-subtitle`,
  date: `${PREFIX}-date`,
  button: `${PREFIX}-button`,
  available: `${PREFIX}-available`,
  notAvailable: `${PREFIX}-notAvailable`,
  warning: `${PREFIX}-warning`,
  success: `${PREFIX}-success`,
  notSet: `${PREFIX}-notSet`,
};

const StyledPanel = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  width: '358px',
  borderRadius: '16px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  background: 'linear-gradient(90deg, #111114 0%, #161516 100%), #0B1928',

  [`& .${classes.button}`]: {
    minWidth: '140px',
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: '26px',
    letterSpacing: '0.75px',
    boxSizing: 'border-box',
  },

  [`& .${classes.available}`]: {
    backgroundColor: '#334a54',
    borderWidth: '1px',
    borderColor: '#7BC7E7',
    borderStyle: 'solid',

    color: 'white',
  },
  [`& .${classes.notAvailable}`]: {
    color: 'rgba(255, 255, 255, 0.5)',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    border: '1px solid #444445',
  },
  [`& .${classes.warning}`]: {
    backgroundColor: theme.palette.warning.main,
    color: 'white',
  },
  [`& .${classes.success}`]: {
    backgroundColor: theme.palette.success.main,
    color: 'white',
  },
  [`& .${classes.notSet}`]: {
    backgroundColor: theme.palette.background.default,
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: 'rgba(255, 255, 255, 0.5)',
  },
}));

const getButtonText = (
  state: AvailabilityStateType,
  jobStatus?: JobBookingStatus
) => {
  if (jobStatus) {
    switch (jobStatus) {
      case JobBookingStatus.Pencilled:
        return 'Pencilled';
      case JobBookingStatus.PencilAccepted:
        return 'Pencil Accepted';
      case JobBookingStatus.Booked:
        return 'Booked';
      case JobBookingStatus.Confirmed:
        return 'Confirmed';
    }
  } else {
    switch (state) {
      case 'NotSet':
        return 'Please Set';
      case 'Available':
        return 'Available';
      case 'NotAvailable':
        return 'Not available';
      default:
        return 'Please Set';
    }
  }
};

const getButtonClass = (
  state: AvailabilityStateType,
  jobStatus?: JobBookingStatus
) => {
  if (jobStatus) {
    switch (jobStatus) {
      case JobBookingStatus.Booked:
      case JobBookingStatus.Pencilled:
        return `${classes.button} ${classes.warning}`;
      case JobBookingStatus.PencilAccepted:
      case JobBookingStatus.Confirmed:
        return `${classes.button} ${classes.success}`;
    }
  } else {
    switch (state) {
      case AvailabilityState.Available:
        return `${classes.button} ${classes.available}`;
      case AvailabilityState.NotAvailable:
        return `${classes.button} ${classes.notAvailable}`;
      default:
        return `${classes.button} ${classes.notSet}`;
    }
  }
};

interface Props {
  availability: UserAvailabilityDTO;
  onPressed: () => void;
}

function Slot({ availability, onPressed }: Props) {
  const { buttonText, buttonClass } = useMemo(() => {
    const buttonText = getButtonText(
      availability.state,
      availability.jobStatus
    );
    const buttonClass = getButtonClass(
      availability.state,
      availability.jobStatus
    );

    return { buttonText, buttonClass };
  }, [availability.state, availability.jobStatus]);

  const handleClick = () => {
    onPressed();
  };

  return (
    <StyledPanel>
      <Typography className={classes.date}>
        {formatDate(availability.date, 'ddd DD MMM')}
      </Typography>
      <Button
        className={buttonClass}
        variant="outlined"
        size="large"
        onClick={handleClick}>
        {buttonText}
      </Button>
    </StyledPanel>
  );
}

export default Slot;
